import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Navbar from "../components/Navbar";
import { BookOpen, Search, Gamepad2, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchBannersByPage } from "@/services/bannersService";
import { PageBanner } from "@/components/common/PageBanner";
import { getAllActiveDigitalContents } from "@/services/digitalContent/publicDigitalContentService";
import { PublicDigitalContentCard } from "@/components/digitalContent/PublicDigitalContentCard";

const GuiasPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGame, setSelectedGame] = useState<string | null>(null);

  const { data: banners } = useQuery({
    queryKey: ["banners", "guias"],
    queryFn: () => fetchBannersByPage("guias"),
  });

  const { data: guides, isLoading, error } = useQuery({
    queryKey: ["public-guides"],
    queryFn: getAllActiveDigitalContents,
  });

  // Lista única de jogos a partir dos guias
  const allGames = useMemo(() => {
    if (!guides) return [];
    return Array.from(
      new Map(
        guides
          .filter((g) => g.game)
          .map((g) => [g.game!.id, g.game!])
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [guides]);

  const filteredGuides = useMemo(() => {
    if (!guides) return [];
    return guides.filter((guide) => {
      const matchesSearch = searchTerm
        ? guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (guide.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (guide.mentor_name || "").toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      const matchesGame = selectedGame ? guide.game_id === selectedGame : true;
      return matchesSearch && matchesGame;
    });
  }, [guides, searchTerm, selectedGame]);

  return (
    <div className="bg-[#0f141c] min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4">
        {/* Banner */}
        <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-8">
          {banners && banners.length > 0 ? (
            <PageBanner banners={banners} />
          ) : (
            <div className="relative w-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#18222F]/50 to-[#0f141c] z-10 pointer-events-none"></div>
              <div className="w-full h-80 bg-gradient-to-r from-[#1A2233] via-[#223342] to-[#2A3749] flex items-center justify-center">
                <div className="text-center text-white relative z-20">
                  <h2 className="text-4xl font-bold mb-4">Guias e Infoprodutos</h2>
                  <p className="text-xl text-gray-300">
                    Aprenda com os melhores através de conteúdos digitais exclusivos
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 pt-6">
          {/* Sidebar com filtros */}
          <div className="w-full lg:w-64 lg:min-w-64">
            <div className="bg-[#181D24] rounded-lg p-4 border border-[#222A35] sticky top-24">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5 text-[#00B6F0]" />
                Filtros
              </h2>

              <div className="mb-6">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                  <Input
                    placeholder="Buscar guia..."
                    className="pl-10 bg-[#1A2233] border-[#2D3748] text-white"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <h3 className="text-[#00B6F0] font-semibold text-sm mb-2 flex items-center gap-2">
                  <Gamepad2 className="h-4 w-4" />
                  Jogos
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={!selectedGame ? "default" : "outline"}
                    className={`cursor-pointer ${
                      !selectedGame
                        ? "bg-[#00B6F0] text-black"
                        : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
                    }`}
                    onClick={() => setSelectedGame(null)}
                  >
                    Todos
                  </Badge>
                  {allGames.map((game) => (
                    <Badge
                      key={game.id}
                      variant={selectedGame === game.id ? "default" : "outline"}
                      className={`cursor-pointer ${
                        selectedGame === game.id
                          ? "bg-[#00B6F0] text-black"
                          : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
                      }`}
                      onClick={() => setSelectedGame(game.id)}
                    >
                      {game.name}
                    </Badge>
                  ))}
                </div>
              </div>

              {selectedGame && (
                <Button
                  variant="link"
                  className="text-[#00B6F0] p-0 text-sm"
                  onClick={() => setSelectedGame(null)}
                >
                  Limpar filtros
                </Button>
              )}
            </div>
          </div>

          {/* Conteúdo principal */}
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-[#00B6F0] mb-6 flex items-center gap-3">
              <BookOpen className="h-7 w-7" />
              Guias
            </h1>

            {isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="w-full h-80 bg-[#181D24] rounded-lg animate-pulse" />
                ))}
              </div>
            )}

            {error && (
              <div className="text-red-500 text-center py-12">
                Erro ao carregar guias. Tente novamente mais tarde.
              </div>
            )}

            {!isLoading && !error && filteredGuides.length === 0 && (
              <div className="text-white text-center py-12">
                {searchTerm || selectedGame
                  ? "Nenhum guia encontrado com os filtros selecionados."
                  : "Nenhum guia disponível ainda."}
              </div>
            )}

            {filteredGuides.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredGuides.map((guide) => (
                  <PublicDigitalContentCard key={guide.id} content={guide} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuiasPage;
